import { arrayToMap } from "@excalidraw/common";
import { isPointWithinBounds, pointFrom } from "@excalidraw/math";
import { doLineSegmentsIntersect } from "@excalidraw/utils/bbox";
import { elementsOverlappingBBox } from "@excalidraw/utils/withinBounds";

import type {
  AppClassProperties,
  AppState,
  StaticCanvasAppState,
} from "@excalidraw/excalidraw/types";

import type { ReadonlySetLike } from "@excalidraw/common/utility-types";

import { getElementsWithinSelection, getSelectedElements } from "./selection";
import { getElementsInGroup, selectGroupsFromGivenElements } from "./groups";

import {
  getElementLineSegments,
  getCommonBounds,
  getElementAbsoluteCoords,
} from "./bounds";
import { mutateElement } from "./mutateElement";
import { getBoundTextElement, getContainerElement } from "./textElement";
import {
  isFrameElement,
  isFrameLikeElement,
  isTextElement,
  hasBoundTextElement,
  isBoundToContainer,
} from "./typeChecks";

import type { ExcalidrawElementsIncludingDeleted } from "./Scene";

import type {
  ElementsMap,
  ElementsMapOrArray,
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
  NonDeleted,
  NonDeletedExcalidrawElement,
} from "./types";

// --------------------------- Frame State ------------------------------------
export const bindElementsToFramesAfterDuplication = (
  nextElements: readonly ExcalidrawElement[],
  origElements: readonly ExcalidrawElement[],
  origIdToDuplicateId: Map<ExcalidrawElement["id"], ExcalidrawElement["id"]>,
) => {
  const nextElementMap = arrayToMap(nextElements) as Map<
    ExcalidrawElement["id"],
    ExcalidrawElement
  >;

  for (const element of origElements) {
    if (element.frameId) {
      // use its frameId to get the new frameId
      const nextElementId = origIdToDuplicateId.get(element.id);
      const nextFrameId = origIdToDuplicateId.get(element.frameId);
      const nextElement = nextElementId && nextElementMap.get(nextElementId);
      if (nextElement) {
        mutateElement(nextElement, nextElementMap, {
          frameId: nextFrameId ?? null,
        });

        // If the duplicated element is a frame and it's being assigned to another frame,
        // ensure its header is hidden since nested frames shouldn't show headers
        if (isFrameLikeElement(nextElement) && nextFrameId) {
          mutateElement(nextElement, nextElementMap, {
            showName: false,
          });
        }
      }
    }
  }
};

export function isElementIntersectingFrame(
  element: ExcalidrawElement,
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) {
  const frameLineSegments = getElementLineSegments(frame, elementsMap);

  const elementLineSegments = getElementLineSegments(element, elementsMap);

  const intersecting = frameLineSegments.some((frameLineSegment) =>
    elementLineSegments.some((elementLineSegment) =>
      doLineSegmentsIntersect(frameLineSegment, elementLineSegment),
    ),
  );

  return intersecting;
}

export const getElementsCompletelyInFrame = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) =>
  omitGroupsContainingFrameLikes(
    getElementsWithinSelection(elements, frame, elementsMap, false),
  ).filter(
    (element) =>
      // Allow regular elements that are not in any frame
      (!isFrameLikeElement(element) && !element.frameId) ||
      // Allow elements already in this frame
      element.frameId === frame.id ||
      // Allow frame-like elements to be nested (but not the frame itself)
      (isFrameLikeElement(element) && element.id !== frame.id),
  );

export const isElementContainingFrame = (
  element: ExcalidrawElement,
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) => {
  return getElementsWithinSelection([frame], element, elementsMap).some(
    (e) => e.id === frame.id,
  );
};

export const getElementsIntersectingFrame = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
) => {
  const elementsMap = arrayToMap(elements);
  return elements.filter((element) =>
    isElementIntersectingFrame(element, frame, elementsMap),
  );
};

export const elementsAreInFrameBounds = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) => {
  const [frameX1, frameY1, frameX2, frameY2] = getElementAbsoluteCoords(
    frame,
    elementsMap,
  );

  const [elementX1, elementY1, elementX2, elementY2] =
    getCommonBounds(elements);

  return (
    frameX1 <= elementX1 &&
    frameY1 <= elementY1 &&
    frameX2 >= elementX2 &&
    frameY2 >= elementY2
  );
};

export const elementOverlapsWithFrame = (
  element: ExcalidrawElement,
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) => {
  return (
    elementsAreInFrameBounds([element], frame, elementsMap) ||
    isElementIntersectingFrame(element, frame, elementsMap) ||
    isElementContainingFrame(element, frame, elementsMap)
  );
};

export const isCursorInFrame = (
  cursorCoords: {
    x: number;
    y: number;
  },
  frame: NonDeleted<ExcalidrawFrameLikeElement>,
  elementsMap: ElementsMap,
) => {
  const [fx1, fy1, fx2, fy2] = getElementAbsoluteCoords(frame, elementsMap);

  return isPointWithinBounds(
    pointFrom(fx1, fy1),
    pointFrom(cursorCoords.x, cursorCoords.y),
    pointFrom(fx2, fy2),
  );
};

export const groupsAreAtLeastIntersectingTheFrame = (
  elements: readonly NonDeletedExcalidrawElement[],
  groupIds: readonly string[],
  frame: ExcalidrawFrameLikeElement,
) => {
  const elementsMap = arrayToMap(elements);
  const elementsInGroup = groupIds.flatMap((groupId) =>
    getElementsInGroup(elements, groupId),
  );

  if (elementsInGroup.length === 0) {
    return true;
  }

  return !!elementsInGroup.find(
    (element) =>
      elementsAreInFrameBounds([element], frame, elementsMap) ||
      isElementIntersectingFrame(element, frame, elementsMap),
  );
};

export const groupsAreCompletelyOutOfFrame = (
  elements: readonly NonDeletedExcalidrawElement[],
  groupIds: readonly string[],
  frame: ExcalidrawFrameLikeElement,
) => {
  const elementsMap = arrayToMap(elements);
  const elementsInGroup = groupIds.flatMap((groupId) =>
    getElementsInGroup(elements, groupId),
  );

  if (elementsInGroup.length === 0) {
    return true;
  }

  return (
    elementsInGroup.find(
      (element) =>
        elementsAreInFrameBounds([element], frame, elementsMap) ||
        isElementIntersectingFrame(element, frame, elementsMap),
    ) === undefined
  );
};

// --------------------------- Frame Utils ------------------------------------

/**
 * Returns a map of frameId to frame elements. Includes empty frames.
 */
export const groupByFrameLikes = (elements: readonly ExcalidrawElement[]) => {
  const frameElementsMap = new Map<
    ExcalidrawElement["id"],
    ExcalidrawElement[]
  >();

  for (const element of elements) {
    const frameId = isFrameLikeElement(element) ? element.id : element.frameId;
    if (frameId && !frameElementsMap.has(frameId)) {
      frameElementsMap.set(frameId, getFrameChildren(elements, frameId));
    }
  }

  return frameElementsMap;
};

export const getFrameChildren = (
  allElements: ElementsMapOrArray,
  frameId: string,
) => {
  const frameChildren: ExcalidrawElement[] = [];
  for (const element of allElements.values()) {
    if (element.frameId === frameId) {
      frameChildren.push(element);
    }
  }
  return frameChildren;
};

/**
 * Get all elements that need to be duplicated when duplicating a frame.
 * This includes all nested children recursively and any bound text elements that belong to those children.
 */
export const getFrameChildrenForDuplication = (
  allElements: ElementsMapOrArray,
  frameId: string,
) => {
  const elementsMap = allElements instanceof Map ? allElements : arrayToMap(allElements);
  const elementsToInclude = new Set<ExcalidrawElement>();
  const processedFrames = new Set<string>();

  const getChildrenRecursively = (currentFrameId: string) => {
    if (processedFrames.has(currentFrameId)) {
      return; // Prevent infinite recursion
    }
    processedFrames.add(currentFrameId);

    const directChildren = getFrameChildren(elementsMap, currentFrameId);

    for (const child of directChildren) {
      elementsToInclude.add(child);

      // If the child is a frame, recursively get its children
      if (isFrameLikeElement(child)) {
        getChildrenRecursively(child.id);
      }
    }
  };

  // Get all nested children recursively
  getChildrenRecursively(frameId);
  
  // Add bound text elements for all children found
  // We need to check bound text elements for all children, not just direct ones
  const allChildrenArray = Array.from(elementsToInclude);
  for (const child of allChildrenArray) {
    // Find bound text elements for this child
    for (const element of elementsMap.values()) {
      if (isBoundToContainer(element) && element.containerId === child.id) {
        elementsToInclude.add(element);
      }
    }
  }
  
  return Array.from(elementsToInclude);
};

/**
 * Checks if a child frame is properly contained within its parent frame
 * to avoid invalid frame hierarchies that can cause rendering/drag issues
 */
export const isNestedFramePositionValid = (
  childFrame: ExcalidrawFrameLikeElement,
  parentFrame: ExcalidrawFrameLikeElement,
): boolean => {
  // A child frame should be fully contained within its parent frame bounds
  return (
    childFrame.x >= parentFrame.x &&
    childFrame.y >= parentFrame.y &&
    childFrame.x + childFrame.width <= parentFrame.x + parentFrame.width &&
    childFrame.y + childFrame.height <= parentFrame.y + parentFrame.height
  );
};

/**
 * Validates and repositions nested frames to ensure they're properly contained
 * within their parent frames. This prevents invalid frame hierarchies.
 *
 * @param allElements - All elements in the scene
 * @param scene - Scene instance for mutating elements
 * @returns A Map of frameIds that have been repositioned (with their old positions)
 */
/**
 * Scene interface with just the mutateElement method we need
 * to avoid circular dependency issues
 */
interface SceneInterface {
  mutateElement: (
    element: ExcalidrawElement,
    updates: Partial<ExcalidrawElement>,
    options?: { informMutation: boolean; isDragging: boolean },
  ) => ExcalidrawElement;
  getNonDeletedElements: () => readonly ExcalidrawElement[];
}

export const validateAndFixNestedFrames = (
  allElements: readonly ExcalidrawElement[],
  scene: SceneInterface,
): Map<
  string,
  { oldX: number; oldY: number; oldWidth: number; oldHeight: number }
> => {
  const changedFrames = new Map<
    string,
    { oldX: number; oldY: number; oldWidth: number; oldHeight: number }
  >();
  const elementsMap = arrayToMap(allElements);

  // Find all frames that have a parent frame
  for (const element of allElements) {
    if (isFrameLikeElement(element) && element.frameId) {
      const parentFrame = elementsMap.get(element.frameId);

      // If parent frame exists and the child frame position is invalid
      if (
        parentFrame &&
        isFrameLikeElement(parentFrame) &&
        !isNestedFramePositionValid(element, parentFrame)
      ) {
        // Only reposition nested frames for auto layout frames
        // For non-auto layout frames, preserve nested frame positions
        const isAutoLayoutFrame =
          parentFrame.type === "frame" &&
          (parentFrame as any).autoLayout?.enabled;

        if (!isAutoLayoutFrame) {
          continue; // Skip repositioning for non-auto layout frames
        }

        // Also skip resizing for nested frames that don't have auto layout themselves
        // This preserves the dimensions of nested frames without auto layout
        const childHasAutoLayout = (element as any).autoLayout?.enabled;

        if (!childHasAutoLayout) {
          continue; // Skip resizing nested frames without auto layout
        }

        if (
          parentFrame.type === "frame" &&
          parentFrame.autoLayout &&
          (parentFrame.autoLayout.resizing.width === "fixed" ||
            parentFrame.autoLayout.resizing.height === "fixed")
        ) {
          continue; // Allow overflow for fixed-size auto-layout frames
        }

        // Save original position for potential undo
        changedFrames.set(element.id, {
          oldX: element.x,
          oldY: element.y,
          oldWidth: element.width,
          oldHeight: element.height,
        });

        // Calculate new position and size to fit within parent frame
        const newX = Math.max(
          parentFrame.x,
          Math.min(
            element.x,
            parentFrame.x + parentFrame.width - element.width,
          ),
        );
        const newY = Math.max(
          parentFrame.y,
          Math.min(
            element.y,
            parentFrame.y + parentFrame.height - element.height,
          ),
        );

        // If the frame is larger than the parent, resize it
        const newWidth = Math.min(
          element.width,
          parentFrame.x + parentFrame.width - newX,
        );
        const newHeight = Math.min(
          element.height,
          parentFrame.y + parentFrame.height - newY,
        );

        // Use the scene's mutateElement to update the element properly
        scene.mutateElement(
          element,
          {
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight,
          },
          { informMutation: true, isDragging: false },
        );
      }
    }
  }

  return changedFrames;
};

export const getAllNestedFrameChildren = (
  allElements: ElementsMapOrArray,
  frameId: string,
): ExcalidrawElement[] => {
  const allChildren: ExcalidrawElement[] = [];
  const processedFrames = new Set<string>();

  const getChildrenRecursively = (currentFrameId: string) => {
    if (processedFrames.has(currentFrameId)) {
      return; // Prevent infinite recursion
    }
    processedFrames.add(currentFrameId);

    const directChildren = getFrameChildren(allElements, currentFrameId);

    for (const child of directChildren) {
      allChildren.push(child);

      // If the child is a frame, recursively get its children
      if (isFrameLikeElement(child)) {
        getChildrenRecursively(child.id);
      }
    }
  };

  getChildrenRecursively(frameId);
  return allChildren;
};

/**
 * Moves child elements when their parent frame is repositioned.
 * This ensures that child elements maintain their relative positions
 * within the frame, even when the frame is moved by auto-layout or other operations.
 *
 * @param originalFrame - The frame before the position change
 * @param updatedFrame - The frame after the position change
 * @param allElements - All elements in the scene
 * @param elementsMap - Map of all elements for efficient lookup
 * @param scene - Scene interface for mutating elements
 * @returns Map of updated child elements
 */
export const moveFrameChildren = (
  originalFrame: ExcalidrawFrameLikeElement,
  updatedFrame: ExcalidrawFrameLikeElement,
  allElements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
  scene: SceneInterface,
): Map<string, ExcalidrawElement> => {
  const updatedChildren = new Map<string, ExcalidrawElement>();

  // Calculate the change in frame position
  const deltaX = updatedFrame.x - originalFrame.x;
  const deltaY = updatedFrame.y - originalFrame.y;

  // If there's no position change, return empty map
  if (deltaX === 0 && deltaY === 0) {
    return updatedChildren;
  }

  // Get all direct children of the frame
  const directChildren = getFrameChildren(allElements, originalFrame.id);

  // Process each child element
  for (const child of directChildren) {
    const latestChild = elementsMap.get(child.id);
    if (!latestChild) {
      continue;
    }

    // Calculate new position for the child element
    const newX = latestChild.x + deltaX;
    const newY = latestChild.y + deltaY;

    // Update the child element's position
    const updatedChild = scene.mutateElement(
      latestChild,
      { x: newX, y: newY },
      { informMutation: false, isDragging: false },
    );

    updatedChildren.set(child.id, updatedChild);

    // If the child has bound text (e.g. a rectangle with a text element),
    // move the bound text by the same delta so that it stays in sync.
    if (hasBoundTextElement(latestChild)) {
      const boundTextElement = getBoundTextElement(latestChild, elementsMap);
      if (boundTextElement) {
        const updatedBoundText = scene.mutateElement(
          boundTextElement,
          { x: boundTextElement.x + deltaX, y: boundTextElement.y + deltaY },
          { informMutation: false, isDragging: false },
        );

        updatedChildren.set(boundTextElement.id, updatedBoundText);
      }
    }

    // If the child is also a frame, recursively move its children
    if (isFrameLikeElement(child)) {
      const childFrameChildren = moveFrameChildren(
        child,
        { ...child, x: newX, y: newY },
        allElements,
        elementsMap,
        scene,
      );

      // Add the nested children to our updated elements map
      for (const [childId, childElement] of childFrameChildren) {
        updatedChildren.set(childId, childElement);
      }
    }
  }

  return updatedChildren;
};

export const getFrameLikeElements = (
  allElements: ExcalidrawElementsIncludingDeleted,
): ExcalidrawFrameLikeElement[] => {
  return allElements.filter((element): element is ExcalidrawFrameLikeElement =>
    isFrameLikeElement(element),
  );
};

/**
 * Returns ExcalidrawFrameElements and non-frame-children elements.
 *
 * Considers children as root elements if they point to a frame parent
 * non-existing in the elements set.
 *
 * Considers non-frame bound elements (container or arrow labels) as root.
 */
export const getRootElements = (
  allElements: ExcalidrawElementsIncludingDeleted,
) => {
  const frameElements = arrayToMap(getFrameLikeElements(allElements));
  return allElements.filter(
    (element) =>
      frameElements.has(element.id) ||
      !element.frameId ||
      !frameElements.has(element.frameId),
  );
};

export const getElementsInResizingFrame = (
  allElements: ExcalidrawElementsIncludingDeleted,
  frame: ExcalidrawFrameLikeElement,
  appState: AppState,
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  const prevElementsInFrame = getFrameChildren(allElements, frame.id);

  // ALWAYS keep existing child elements in the frame
  // This preserves frame hierarchy when frames are resized smaller
  const nextElementsInFrame = new Set<ExcalidrawElement>(prevElementsInFrame);

  // Find elements that are completely within the new frame bounds
  const elementsCompletelyInFrame = getElementsCompletelyInFrame(
    allElements,
    frame,
    elementsMap,
  );

  // Only ADD new elements that weren't previously in this frame
  for (const element of elementsCompletelyInFrame) {
    if (!prevElementsInFrame.includes(element)) {
      nextElementsInFrame.add(element);
    }
  }

  // Handle groups: if a new element is part of a group, include the entire group
  // if all group members are within frame bounds
  const newElements = Array.from(elementsCompletelyInFrame).filter(
    (element) =>
      !prevElementsInFrame.includes(element) && element.groupIds.length > 0,
  );

  if (newElements.length > 0) {
    const groupIds = selectGroupsFromGivenElements(newElements, appState);

    for (const [id, isSelected] of Object.entries(groupIds)) {
      if (isSelected) {
        const elementsInGroup = getElementsInGroup(allElements, id);

        if (elementsAreInFrameBounds(elementsInGroup, frame, elementsMap)) {
          for (const element of elementsInGroup) {
            nextElementsInFrame.add(element);
          }
        }
      }
    }
  }

  return [...nextElementsInFrame].filter((element) => {
    return !(isTextElement(element) && element.containerId);
  });
};

export const getElementsInNewFrame = (
  elements: ExcalidrawElementsIncludingDeleted,
  frame: ExcalidrawFrameLikeElement,
  elementsMap: ElementsMap,
) => {
  return omitPartialGroups(
    omitGroupsContainingFrameLikes(
      elements,
      getElementsCompletelyInFrame(elements, frame, elementsMap),
    ),
    frame,
    elementsMap,
  );
};

export const omitPartialGroups = (
  elements: ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
  allElementsMap: ElementsMap,
) => {
  const elementsToReturn = [];
  const checkedGroups = new Map<string, boolean>();

  for (const element of elements) {
    let shouldOmit = false;
    if (element.groupIds.length > 0) {
      // if some partial group should be omitted, then all elements in that group should be omitted
      if (element.groupIds.some((gid) => checkedGroups.get(gid))) {
        shouldOmit = true;
      } else {
        const allElementsInGroup = new Set(
          element.groupIds.flatMap((gid) =>
            getElementsInGroup(allElementsMap, gid),
          ),
        );

        shouldOmit = !elementsAreInFrameBounds(
          Array.from(allElementsInGroup),
          frame,
          allElementsMap,
        );
      }

      element.groupIds.forEach((gid) => {
        checkedGroups.set(gid, shouldOmit);
      });
    }

    if (!shouldOmit) {
      elementsToReturn.push(element);
    }
  }

  return elementsToReturn;
};

export const getContainingFrame = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
) => {
  if (!element.frameId) {
    return null;
  }
  return (elementsMap.get(element.frameId) ||
    null) as null | ExcalidrawFrameLikeElement;
};

// --------------------------- Frame Operations -------------------------------

/** */
export const filterElementsEligibleAsFrameChildren = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
) => {
  const otherFrames = new Set<ExcalidrawFrameLikeElement["id"]>();
  const elementsMap = arrayToMap(elements);
  elements = omitGroupsContainingFrameLikes(elements);

  for (const element of elements) {
    if (isFrameLikeElement(element) && element.id !== frame.id) {
      otherFrames.add(element.id);
    }
  }

  const processedGroups = new Set<ExcalidrawElement["id"]>();

  const eligibleElements: ExcalidrawElement[] = [];

  for (const element of elements) {
    // Skip only if it's the same frame (prevent adding a frame to itself)
    if (isFrameLikeElement(element) && element.id === frame.id) {
      continue;
    }

    // Don't add children of other frames if those frames are not being added
    if (
      element.frameId &&
      otherFrames.has(element.frameId) &&
      !isFrameLikeElement(element)
    ) {
      continue;
    }

    if (element.groupIds.length) {
      const shallowestGroupId = element.groupIds.at(-1)!;
      if (!processedGroups.has(shallowestGroupId)) {
        processedGroups.add(shallowestGroupId);
        const groupElements = getElementsInGroup(elements, shallowestGroupId);
        if (
          groupElements.some((el) =>
            elementOverlapsWithFrame(el, frame, elementsMap),
          )
        ) {
          for (const child of groupElements) {
            eligibleElements.push(child);
          }
        }
      }
    } else {
      const overlaps = elementOverlapsWithFrame(element, frame, elementsMap);
      if (overlaps) {
        eligibleElements.push(element);
      }
    }
  }

  return eligibleElements;
};

/**
 * Retains (or repairs for target frame) the ordering invriant where children
 * elements come right before the parent frame:
 * [el, el, child, child, frame, el]
 *
 * @returns mutated allElements (same data structure)
 */
export const addElementsToFrame = <T extends ElementsMapOrArray>(
  allElements: T,
  elementsToAdd: NonDeletedExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
  appState: AppState,
): T => {
  const elementsMap = arrayToMap(allElements);
  const currTargetFrameChildrenMap = new Map<ExcalidrawElement["id"], true>();
  for (const element of allElements.values()) {
    if (element.frameId === frame.id) {
      currTargetFrameChildrenMap.set(element.id, true);
    }
  }

  const suppliedElementsToAddSet = new Set(elementsToAdd.map((el) => el.id));

  const finalElementsToAdd: ExcalidrawElement[] = [];

  const otherFrames = new Set<ExcalidrawFrameLikeElement["id"]>();

  for (const element of elementsToAdd) {
    if (isFrameLikeElement(element) && element.id !== frame.id) {
      otherFrames.add(element.id);
    }
  }

  // - add bound text elements if not already in the array
  // - filter out elements that are already in the frame
  for (const element of omitGroupsContainingFrameLikes(
    allElements,
    elementsToAdd,
  )) {
    // Skip only if it's the same frame (prevent adding a frame to itself)
    if (isFrameLikeElement(element) && element.id === frame.id) {
      continue;
    }

    // Don't add children of other frames if those frames are not being added
    if (
      element.frameId &&
      otherFrames.has(element.frameId) &&
      !isFrameLikeElement(element)
    ) {
      continue;
    }

    // if the element is already in another frame (which is also in elementsToAdd),
    // it means that frame and children are selected at the same time
    // => keep original frame membership, do not add to the target frame
    if (
      element.frameId &&
      appState.selectedElementIds[element.id] &&
      appState.selectedElementIds[element.frameId]
    ) {
      continue;
    }

    // Prevent cyclic frame nesting: don't add a frame that already contains
    // the target frame as a descendant (would create a cycle)
    if (
      isFrameLikeElement(element) &&
      getAllNestedFrameChildren(allElements, element.id).some(
        (child) => child.id === frame.id,
      )
    ) {
      // skip to avoid creating cycle
      continue;
    }

    if (!currTargetFrameChildrenMap.has(element.id)) {
      finalElementsToAdd.push(element);
    }

    const boundTextElement = getBoundTextElement(element, elementsMap);
    if (
      boundTextElement &&
      !suppliedElementsToAddSet.has(boundTextElement.id) &&
      !currTargetFrameChildrenMap.has(boundTextElement.id)
    ) {
      // Don't add bound text to finalElementsToAdd - it should not get frameId
      // Bound text should only be positioned relative to its container via containerId
    }
  }

  for (const element of finalElementsToAdd) {
    mutateElement(element, elementsMap, {
      frameId: frame.id,
    });

    // Handle bound text separately - ensure it doesn't get frameId
    const boundTextElement = getBoundTextElement(element, elementsMap);
    if (boundTextElement && boundTextElement.frameId === frame.id) {
      // Remove frameId from bound text - it should only be bound via containerId
      mutateElement(boundTextElement, elementsMap, {
        frameId: null,
      });
    }

    // If the element being added is a frame, automatically hide its header
    // since nested frames shouldn't show headers to avoid visual clutter
    if (isFrameLikeElement(element)) {
      mutateElement(element, elementsMap, {
        showName: false,
      });

      // Only reposition nested frames for auto layout frames
      // For non-auto layout frames, preserve nested frame positions
      const isAutoLayoutFrame =
        frame.type === "frame" && (frame as any).autoLayout?.enabled;

      // Also check if the nested frame itself has auto layout
      // Preserve dimensions of nested frames without auto layout
      const childHasAutoLayout = (element as any).autoLayout?.enabled;

      // Check if the frame would be positioned outside its parent
      if (
        isAutoLayoutFrame &&
        childHasAutoLayout &&
        !isNestedFramePositionValid(element, frame)
      ) {
        // Calculate new position and size to fit within parent frame
        const newX = Math.max(
          frame.x,
          Math.min(element.x, frame.x + frame.width - element.width),
        );
        const newY = Math.max(
          frame.y,
          Math.min(element.y, frame.y + frame.height - element.height),
        );

        // If the frame is larger than the parent, resize it
        const newWidth = Math.min(element.width, frame.x + frame.width - newX);
        const newHeight = Math.min(
          element.height,
          frame.y + frame.height - newY,
        );

        // Update the element position
        mutateElement(element, elementsMap, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      }
    }
  }

  return allElements;
};

export const removeElementsFromFrame = (
  elementsToRemove: ReadonlySetLike<NonDeletedExcalidrawElement>,
  elementsMap: ElementsMap,
) => {
  const _elementsToRemove = new Map<
    ExcalidrawElement["id"],
    ExcalidrawElement
  >();

  const toRemoveElementsByFrame = new Map<
    ExcalidrawFrameLikeElement["id"],
    ExcalidrawElement[]
  >();

  for (const element of elementsToRemove) {
    if (element.frameId) {
      _elementsToRemove.set(element.id, element);

      const arr = toRemoveElementsByFrame.get(element.frameId) || [];
      arr.push(element);

      const boundTextElement = getBoundTextElement(element, elementsMap);
      if (boundTextElement) {
        _elementsToRemove.set(boundTextElement.id, boundTextElement);
        arr.push(boundTextElement);
      }

      toRemoveElementsByFrame.set(element.frameId, arr);
    }
  }

  for (const [, element] of _elementsToRemove) {
    mutateElement(element, elementsMap, {
      frameId: null,
    });

    // If the element being removed is a frame, restore its header visibility
    // since it's no longer nested and should show its header again
    if (isFrameLikeElement(element)) {
      mutateElement(element, elementsMap, {
        showName: true,
      });
    }
  }
};

export const removeAllElementsFromFrame = <T extends ExcalidrawElement>(
  allElements: readonly T[],
  frame: ExcalidrawFrameLikeElement,
) => {
  const elementsInFrame = getFrameChildren(allElements, frame.id);
  removeElementsFromFrame(elementsInFrame, arrayToMap(allElements));
  return allElements;
};

export const replaceAllElementsInFrame = <T extends ExcalidrawElement>(
  allElements: readonly T[],
  nextElementsInFrame: ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
  app: AppClassProperties,
): T[] => {
  return addElementsToFrame(
    removeAllElementsFromFrame(allElements, frame),
    nextElementsInFrame,
    frame,
    app.state,
  ).slice();
};

/** does not mutate elements, but returns new ones */
export const updateFrameMembershipOfSelectedElements = <
  T extends ElementsMapOrArray,
>(
  allElements: T,
  appState: AppState,
  app: AppClassProperties,
) => {
  const selectedElements = app.scene.getSelectedElements({
    selectedElementIds: appState.selectedElementIds,
    // supplying elements explicitly in case we're passed non-state elements
    elements: allElements,
  });
  const elementsToFilter = new Set<ExcalidrawElement>(selectedElements);

  if (appState.editingGroupId) {
    for (const element of selectedElements) {
      if (element.groupIds.length === 0) {
        elementsToFilter.add(element);
      } else {
        element.groupIds
          .flatMap((gid) => getElementsInGroup(allElements, gid))
          .forEach((element) => elementsToFilter.add(element));
      }
    }
  }

  const elementsToRemove = new Set<ExcalidrawElement>();

  const elementsMap = arrayToMap(allElements);

  elementsToFilter.forEach((element) => {
    if (
      element.frameId &&
      !isFrameLikeElement(element) &&
      !isElementInFrame(element, elementsMap, appState)
    ) {
      // Check if the parent frame has auto layout with hug sizing
      const parentFrame = elementsMap.get(element.frameId);
      if (
        parentFrame &&
        parentFrame.type === "frame" &&
        (parentFrame as any).autoLayout?.enabled
      ) {
        const autoLayout = (parentFrame as any).autoLayout;
        // Prevent removing elements from frames with hug sizing to maintain layout integrity
        if (
          autoLayout.resizing.width === "hug" ||
          autoLayout.resizing.height === "hug"
        ) {
          return; // Skip removal - keep element in frame
        }
      }
      
      elementsToRemove.add(element);
    }
  });

  if (elementsToRemove.size > 0) {
    removeElementsFromFrame(elementsToRemove, elementsMap);
  }
  return allElements;
};

/**
 * filters out elements that are inside groups that contain a frame element
 * anywhere in the group tree
 */
export const omitGroupsContainingFrameLikes = (
  allElements: ElementsMapOrArray,
  /** subset of elements you want to filter. Optional perf optimization so we
   * don't have to filter all elements unnecessarily
   */
  selectedElements?: readonly ExcalidrawElement[],
) => {
  const uniqueGroupIds = new Set<string>();
  const elements = selectedElements || allElements;

  for (const el of elements.values()) {
    const topMostGroupId = el.groupIds[el.groupIds.length - 1];
    if (topMostGroupId) {
      uniqueGroupIds.add(topMostGroupId);
    }
  }

  const rejectedGroupIds = new Set<string>();
  for (const groupId of uniqueGroupIds) {
    if (
      getElementsInGroup(allElements, groupId).some((el) =>
        isFrameLikeElement(el),
      )
    ) {
      rejectedGroupIds.add(groupId);
    }
  }

  const ret: ExcalidrawElement[] = [];

  for (const element of elements.values()) {
    if (!rejectedGroupIds.has(element.groupIds[element.groupIds.length - 1])) {
      ret.push(element);
    }
  }

  return ret;
};

/**
 * depending on the appState, return target frame, which is the frame the given element
 * is going to be added to or remove from
 */
export const getTargetFrame = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
  appState: StaticCanvasAppState,
) => {
  const _element = isTextElement(element)
    ? getContainerElement(element, elementsMap) || element
    : element;

  // if the element and its containing frame are both selected, then
  // the containing frame is the target frame
  if (
    _element.frameId &&
    appState.selectedElementIds[_element.id] &&
    appState.selectedElementIds[_element.frameId]
  ) {
    return getContainingFrame(_element, elementsMap);
  }

  return appState.selectedElementIds[_element.id] &&
    appState.selectedElementsAreBeingDragged
    ? appState.frameToHighlight
    : getContainingFrame(_element, elementsMap);
};

// TODO: this a huge bottleneck for large scenes, optimise
// given an element, return if the element is in some frame
export const isElementInFrame = (
  element: ExcalidrawElement,
  allElementsMap: ElementsMap,
  appState: StaticCanvasAppState,
  opts?: {
    targetFrame?: ExcalidrawFrameLikeElement;
    checkedGroups?: Map<string, boolean>;
  },
) => {
  const frame =
    opts?.targetFrame ?? getTargetFrame(element, allElementsMap, appState);

  if (!frame) {
    return false;
  }

  const _element = isTextElement(element)
    ? getContainerElement(element, allElementsMap) || element
    : element;

  const setGroupsInFrame = (isInFrame: boolean) => {
    if (opts?.checkedGroups) {
      _element.groupIds.forEach((groupId) => {
        opts.checkedGroups?.set(groupId, isInFrame);
      });
    }
  };

  if (
    // if the element is not selected, or it is selected but not being dragged,
    // frame membership won't update, so return true
    !appState.selectedElementIds[_element.id] ||
    !appState.selectedElementsAreBeingDragged ||
    // if both frame and element are selected, won't update membership, so return true
    (appState.selectedElementIds[_element.id] &&
      appState.selectedElementIds[frame.id])
  ) {
    return true;
  }

  if (_element.groupIds.length === 0) {
    return elementOverlapsWithFrame(_element, frame, allElementsMap);
  }

  for (const gid of _element.groupIds) {
    if (opts?.checkedGroups?.has(gid)) {
      return opts.checkedGroups.get(gid)!!;
    }
  }

  const allElementsInGroup = new Set(
    _element.groupIds
      .filter((gid) => {
        if (opts?.checkedGroups) {
          return !opts.checkedGroups.has(gid);
        }
        return true;
      })
      .flatMap((gid) => getElementsInGroup(allElementsMap, gid)),
  );

  if (appState.editingGroupId && appState.selectedElementsAreBeingDragged) {
    const selectedElements = new Set(
      getSelectedElements(allElementsMap, appState),
    );

    const editingGroupOverlapsFrame = appState.frameToHighlight !== null;

    if (editingGroupOverlapsFrame) {
      return true;
    }

    selectedElements.forEach((selectedElement) => {
      allElementsInGroup.delete(selectedElement);
    });
  }

  for (const elementInGroup of allElementsInGroup) {
    if (isFrameLikeElement(elementInGroup)) {
      setGroupsInFrame(false);
      return false;
    }
  }

  for (const elementInGroup of allElementsInGroup) {
    if (elementOverlapsWithFrame(elementInGroup, frame, allElementsMap)) {
      setGroupsInFrame(true);
      return true;
    }
  }

  return false;
};

export const shouldApplyFrameClip = (
  element: ExcalidrawElement,
  frame: ExcalidrawFrameLikeElement,
  appState: StaticCanvasAppState,
  elementsMap: ElementsMap,
  checkedGroups?: Map<string, boolean>,
) => {
  // Always apply clipping for fixed-size frames (auto layout with fixed resizing)
  const isFixedSizeFrame =
    frame.type === "frame" &&
    frame.autoLayout &&
    (frame.autoLayout.resizing.width === "fixed" ||
      frame.autoLayout.resizing.height === "fixed");

  // ENHANCED CLIPPING LOGIC: For child elements of ANY frame, always apply clipping
  // This ensures that child elements are masked/clipped by their parent frame boundaries
  if (element.frameId === frame.id) {
    for (const groupId of element.groupIds) {
      checkedGroups?.set(groupId, true);
    }
    return true;
  }

  // For fixed-size frames, clip all child elements regardless of their position
  if (isFixedSizeFrame) {
    for (const groupId of element.groupIds) {
      checkedGroups?.set(groupId, true);
    }
    return true;
  }

  // For non-child elements, only apply clipping if frameRendering.clip is enabled
  // This preserves the existing behavior for elements that are not direct children
  if (
    !isFixedSizeFrame &&
    (!appState.frameRendering || !appState.frameRendering.clip)
  ) {
    return false;
  }

  // for individual elements, only clip when the element is
  // a. overlapping with the frame, or
  // b. containing the frame, for example when an element is used as a background
  //    and is therefore bigger than the frame and completely contains the frame
  const shouldClipElementItself =
    isElementIntersectingFrame(element, frame, elementsMap) ||
    isElementContainingFrame(element, frame, elementsMap);

  if (shouldClipElementItself) {
    for (const groupId of element.groupIds) {
      checkedGroups?.set(groupId, true);
    }

    return true;
  }

  // if an element is outside the frame, but is part of a group that has some elements
  // "in" the frame, we should clip the element
  if (
    !shouldClipElementItself &&
    element.groupIds.length > 0 &&
    !elementsAreInFrameBounds([element], frame, elementsMap)
  ) {
    let shouldClip = false;

    // if no elements are being dragged, we can skip the geometry check
    // because we know if the element is in the given frame or not
    if (!appState.selectedElementsAreBeingDragged) {
      shouldClip = element.frameId === frame.id;
      for (const groupId of element.groupIds) {
        checkedGroups?.set(groupId, shouldClip);
      }
    } else {
      shouldClip = isElementInFrame(element, elementsMap, appState, {
        targetFrame: frame,
        checkedGroups,
      });
    }

    for (const groupId of element.groupIds) {
      checkedGroups?.set(groupId, shouldClip);
    }

    return shouldClip;
  }

  return false;
};

const DEFAULT_FRAME_NAME = "Frame";
const DEFAULT_AI_FRAME_NAME = "AI Frame";

export const getDefaultFrameName = (element: ExcalidrawFrameLikeElement) => {
  // TODO name frames "AI" only if specific to AI frames
  return isFrameElement(element) ? DEFAULT_FRAME_NAME : DEFAULT_AI_FRAME_NAME;
};

export const getFrameLikeTitle = (element: ExcalidrawFrameLikeElement) => {
  return element.name === null ? getDefaultFrameName(element) : element.name;
};

export const getElementsOverlappingFrame = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
) => {
  return (
    elementsOverlappingBBox({
      elements,
      bounds: frame,
      type: "overlap",
    })
      // removes elements who are overlapping, but are in a different frame,
      // and thus invisible in target frame
      .filter((el) => !el.frameId || el.frameId === frame.id)
  );
};

export const frameAndChildrenSelectedTogether = (
  selectedElements: readonly ExcalidrawElement[],
) => {
  const selectedElementsMap = arrayToMap(selectedElements);

  return (
    selectedElements.length > 1 &&
    selectedElements.some(
      (element) => element.frameId && selectedElementsMap.has(element.frameId),
    )
  );
};

export const getFrameAncestors = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawFrameLikeElement[] => {
  const ancestors: ExcalidrawFrameLikeElement[] = [];
  let currentId = element.frameId;
  while (currentId) {
    const current = elementsMap.get(currentId);
    if (!current || !isFrameLikeElement(current)) {
      break;
    }
    ancestors.push(current);
    currentId = current.frameId;
  }
  return ancestors.reverse(); // outer to inner
};
